// RegionLockContext — when a non-premium user lands on
// /app?test_region=<name>, the region is locked for the session.
// Every premium-gated control (country picker, municipality search,
// hydrogen tab, coord-lookup outside bbox, map pan outside bbox)
// reads from this context to decide whether to render / how to
// behave.
//
// Premium users see the FULL product regardless of URL params —
// `locked: false`. Non-premium users with a recognised
// `?test_region=` param get `locked: true, region: '<name>',
// bbox: {min_lat, max_lat, min_lon, max_lon}`. Non-premium users
// who land on bare /app are auto-redirected to ?test_region=stavanger
// by AppShell (see App.jsx) — by the time RegionLockProvider sees
// them, the URL always has a test_region param.
//
// The region is captured at mount and persists in state — stripping
// the URL param after mount does NOT unlock. The lock is also
// echoed to the backend as a `region` body field on every search
// (see SiteFinder.jsx), so the tier_enforcement clamp matches.

import React, { createContext, useContext, useMemo } from 'react';
import { useIsPremium } from './PremiumContext';

// Known free-tier regions. Stavanger is the canonical free region
// (Implementation Brief 2026-06-07); the rest are kept as diagnostic
// overrides usable by premium devs via the same URL param.
//
// Keep these numbers in sync with backend/app/core/tier_enforcement.py
// FREE_TIER_BBOX (which is the Stavanger row) — when one moves the
// other must move too.
export const REGION_BBOXES = {
  stavanger:  { min_lat: 58.70, max_lat: 59.30, min_lon: 5.30, max_lon: 6.20 },
  vestlandet: { min_lat: 61.04, max_lat: 62.55, min_lon: 5.63, max_lon: 8.00 },
  arendal:    { min_lat: 58.20, max_lat: 58.70, min_lon: 8.40, max_lon: 9.20 },
  oslo:       { min_lat: 59.70, max_lat: 60.10, min_lon: 10.40, max_lon: 11.20 },
  smola:      { min_lat: 63.30, max_lat: 63.70, min_lon: 7.90, max_lon: 8.70 },
  trondheim:  { min_lat: 63.20, max_lat: 63.60, min_lon: 10.10, max_lon: 11.00 },
};

// Human-readable labels for header / banner copy.
export const REGION_LABELS = {
  stavanger:  'Stavanger',
  vestlandet: 'Vestlandet',
  arendal:    'Arendal',
  oslo:       'Oslo',
  smola:      'Smøla',
  trondheim:  'Trondheim',
};

// Free tier default — the canonical region for new sign-ups and any
// premium-not-yet-granted user landing on bare /app.
export const DEFAULT_FREE_REGION = 'stavanger';

const RegionLockContext = createContext({
  locked: false,
  region: null,
  label: null,
  bbox: null,
});

export function RegionLockProvider({ children }) {
  const { isPremium, loading } = useIsPremium();

  // Capture the region from the URL ONCE at mount, then hold it in
  // useMemo for the session. Strip the param later → no unlock.
  const value = useMemo(() => {
    if (loading) return { locked: false, region: null, label: null, bbox: null };
    if (isPremium) return { locked: false, region: null, label: null, bbox: null };
    let region = null;
    try {
      const qs = new URLSearchParams(window.location.search);
      const raw = (qs.get('test_region') || '').toLowerCase();
      if (raw && REGION_BBOXES[raw]) region = raw;
    } catch { /* SSR / sandboxed */ }
    if (!region) {
      // Defence in depth — AppShell auto-redirects bare /app for
      // non-premium users, but if that effect hasn't fired yet (or
      // the user disabled it via a browser extension), fall back to
      // the default region so the lock never silently disappears.
      region = DEFAULT_FREE_REGION;
    }
    return {
      locked: true,
      region,
      label: REGION_LABELS[region] || region,
      bbox: REGION_BBOXES[region],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium, loading]);

  return (
    <RegionLockContext.Provider value={value}>
      {children}
    </RegionLockContext.Provider>
  );
}

export function useRegionLock() {
  return useContext(RegionLockContext);
}
